/**
 * Modèle interne normalisé.
 *
 * Toutes les syntaxes supportées (CII / Factur-X / XRechnung, UBL 2.1) sont
 * converties vers cette structure unique. Les champs sont nommés d'après les
 * codes BT (Business Terms) de la norme EN 16931 afin que l'UI, les libellés
 * français et la matrice de couverture parlent tous le même langage.
 */
import type Decimal from 'decimal.js-light';

export type Dec = Decimal;

/** Profils Factur-X / ZUGFeRD 2, plus XRechnung (CIUS d'EN 16931). */
export type InvoiceProfile =
  | 'MINIMUM'
  | 'BASIC_WL'
  | 'BASIC'
  | 'EN16931'
  | 'EXTENDED'
  | 'XRECHNUNG'
  | 'UNKNOWN';

/** Rang d'exigence : plus le rang est élevé, plus le profil exige de champs. */
export const PROFILE_RANK: Record<InvoiceProfile, number> = {
  MINIMUM: 0,
  BASIC_WL: 1,
  BASIC: 2,
  EN16931: 3,
  XRECHNUNG: 3,
  EXTENDED: 4,
  UNKNOWN: -1,
};

export type ProfileFamily = 'facturx' | 'zugferd2' | 'xrechnung' | 'en16931' | 'peppol' | 'zugferd1' | 'unknown';

export interface ProfileInfo {
  profile: InvoiceProfile;
  /** Valeur brute de BT-24 (CustomizationID / GuidelineSpecifiedDocumentContextParameter). */
  urn: string | null;
  family: ProfileFamily;
  /** Version déclarée pour XRechnung (ex. « 3.0 »). */
  version: string | null;
  match: 'exact' | 'prefix' | 'legacy' | 'none';
  notes: string[];
}

export type Syntax = 'CII' | 'UBL' | 'UNKNOWN';

export type DocumentKind = 'invoice' | 'credit-note' | 'unknown';

export interface PostalAddress {
  line1?: string;
  line2?: string;
  line3?: string;
  postcode?: string;
  city?: string;
  country?: string;
}

export interface PartyIdentifier {
  scheme: string;
  value: string;
}

export interface PartyContact {
  name?: string;
  phone?: string;
  email?: string;
}

export interface Party {
  name?: string;
  tradingName?: string;
  legalRegistrationId?: string;
  identifiers: PartyIdentifier[];
  vatId?: string;
  taxId?: string;
  siret?: string;
  electronicAddress?: PartyIdentifier;
  address?: PostalAddress;
  contact?: PartyContact;
}

export interface InvoiceLine {
  /** BT-126 */
  id?: string;
  /** BT-153 */
  name?: string;
  /** BT-154 */
  description?: string;
  /** BT-155 */
  sellerItemId?: string;
  /** BT-129 */
  quantity?: Dec;
  /** BT-130 */
  unitCode?: string;
  /** BT-146 (prix unitaire net) */
  unitPrice?: Dec;
  /** BT-149 */
  priceBaseQuantity?: Dec;
  /** BT-148 (prix unitaire brut) */
  grossPrice?: Dec;
  /** BT-147 (remise ligne) */
  itemDiscount?: Dec;
  /** BT-131 */
  lineNetAmount?: Dec;
  /** BT-151 */
  vatCategory?: string;
  /** BT-152 */
  vatRate?: Dec;
  note?: string;
}

export interface TaxBreakdown {
  /** BT-118 */
  category?: string;
  /** BT-119 */
  rate?: Dec;
  /** BT-116 */
  taxableAmount?: Dec;
  /** BT-117 */
  taxAmount?: Dec;
  /** BT-120 */
  exemptionReason?: string;
  /** BT-121 */
  exemptionCode?: string;
}

export interface MonetaryTotals {
  /** BT-106 */
  lineNet?: Dec;
  /** BT-107 */
  allowanceTotal?: Dec;
  /** BT-108 */
  chargeTotal?: Dec;
  /** BT-109 */
  taxBasis?: Dec;
  /** BT-110 */
  taxTotal?: Dec;
  /** BT-111 */
  taxTotalAccounting?: Dec;
  /** BT-112 */
  grandTotal?: Dec;
  /** BT-113 */
  prepaid?: Dec;
  /** BT-114 */
  rounding?: Dec;
  /** BT-115 */
  duePayable?: Dec;
}

export interface PaymentInfo {
  /** BT-81 */
  meansCode?: string;
  /** BT-82 */
  meansText?: string;
  /** BT-84 */
  iban?: string;
  /** BT-86 */
  bic?: string;
  /** BT-9 */
  dueDate?: string;
  /** BT-20 */
  terms?: string;
  /** BT-89 */
  mandateReference?: string;
  /** BT-83 */
  remittanceInformation?: string;
}

export interface InvoiceDocument {
  syntax: Syntax;
  /** Version de la syntaxe détectée : « CII D16B », « UBL 2.1 ». */
  syntaxVersion: string | null;
  profile: ProfileInfo;
  documentType: DocumentKind;
  /** BT-3 */
  documentTypeCode?: string;
  /** BT-1 */
  number?: string;
  /** BT-2 (ISO 8601, AAAA-MM-JJ) */
  issueDate?: string;
  /** BT-9 */
  dueDate?: string;
  /** BT-5 */
  currency?: string;
  /** BT-6 */
  taxCurrency?: string;
  /** BT-23 */
  businessProcess?: string;
  /** BT-10 */
  buyerReference?: string;
  /** BT-13 */
  orderReference?: string;
  /** BT-14 */
  salesOrderReference?: string;
  /** BT-12 */
  contractReference?: string;
  /** BT-72 */
  deliveryDate?: string;
  /** BT-22 */
  note?: string;
  seller: Party;
  buyer: Party;
  payee?: Party;
  lines: InvoiceLine[];
  taxes: TaxBreakdown[];
  totals: MonetaryTotals;
  payment: PaymentInfo;
  notes: string[];
}

export type WarningSeverity = 'info' | 'warn' | 'error';

export interface ExtractionWarning {
  code: string;
  severity: WarningSeverity;
  message: string;
}

/** Métadonnées PDF utiles (XMP / pièces jointes). */
export interface PdfMeta {
  attachmentCount: number;
  attachmentNames: string[];
  /** Origine de l'XML retenu. */
  xmlSource: 'name-tree' | 'af' | null;
  /** Nom de la pièce jointe retenue (ex. « factur-x.xml »). */
  xmlFilename: string | null;
  xmpProfileLevel: string | null;
  xmpDocumentFileName: string | null;
  xmpVersion: string | null;
  pdfaPart: string | null;
  pdfaConformance: string | null;
  producer: string | null;
}

export interface ExtractionResult {
  doc: InvoiceDocument;
  /** Pièce jointe retenue et sa taille (diagnostic). */
  source: {
    kind: 'pdf' | 'xml';
    filename: string | null;
    byteLength: number;
  };
  pdf: PdfMeta | null;
  warnings: ExtractionWarning[];
}

export function emptyParty(): Party {
  return { identifiers: [] };
}

export function emptyDocument(syntax: Syntax): InvoiceDocument {
  return {
    syntax,
    syntaxVersion: null,
    profile: { profile: 'UNKNOWN', urn: null, family: 'unknown', version: null, match: 'none', notes: [] },
    documentType: 'unknown',
    seller: emptyParty(),
    buyer: emptyParty(),
    lines: [],
    taxes: [],
    totals: {},
    payment: {},
    notes: [],
  };
}
